import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MultiCombobox, Combobox } from "@/components/TechCombobox";
import { TECH_CAPABILITIES } from "@/lib/reconConstants";
import { DatabaseZap, Plus, Archive, Pencil, Cloud, ShieldCheck, Inbox, Check } from "lucide-react";

const KINDS = [
  { k: "product", label: "Products / Systems" },
  { k: "vendor", label: "Vendors" },
  { k: "environment", label: "Environments" },
  { k: "authorization", label: "Authorizations" },
  { k: "evidence_source", label: "Evidence Sources" },
  { k: "system_category", label: "System Categories" },
];
const Badge = ({ className, children }) => (<span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>);

export default function TechCatalogManager({ me }) {
  const [kind, setKind] = useState("product");
  const [items, setItems] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resolving, setResolving] = useState(null);
  const canEdit = !!me?.caps?.create;    // super_admin + compliance_admin
  const canArchive = !!me?.caps?.delete; // super_admin only

  const load = useCallback(() => {
    api.get("/blueprints/tech-catalog", { params: { kind } }).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [kind]);
  const loadProposals = useCallback(() => {
    api.get("/blueprints/tech-catalog/proposals", { params: { status: "pending" } }).then((r) => setProposals(r.data.proposals || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProposals(); }, [loadProposals]);

  const archive = async (it) => {
    if (!window.confirm(`Archive "${it.value}"? It will no longer appear in catalog dropdowns.`)) return;
    try { await api.post(`/blueprints/tech-catalog/${it.id}/archive`); toast.success("Archived."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };

  return (
    <section className="rounded-md border border-border bg-card" data-testid="tech-catalog-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <DatabaseZap className="h-4 w-4 text-gold-dark" aria-hidden="true" />
          <h2 className="font-display font-bold text-navy">Technology Catalog</h2>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <ShieldCheck className="h-4 w-4" /> {canArchive ? "Super Admin" : canEdit ? "Compliance Admin" : "Read-only"}
        </span>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Reusable systems, vendors, environments, authorizations and evidence sources that power the Customer Technology Profile dropdowns. Compliance Admins and Super Admins can add and edit entries; only Super Admins can archive. Reviewer-proposed customer-specific values appear in the review queue below.
        </p>

        {proposals.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-4" data-testid="tech-proposals">
            <div className="flex items-center gap-2 mb-2"><Inbox className="h-4 w-4 text-amber-700" /><h3 className="font-display font-bold text-navy text-sm">Pending Catalog Review ({proposals.length})</h3></div>
            <div className="space-y-2">
              {proposals.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-white px-3 py-2 text-xs" data-testid="tech-proposal-row">
                  <div>
                    <span className="font-medium text-navy">{p.value}</span>
                    <span className="text-muted-foreground"> · {KINDS.find((x) => x.k === p.kind)?.label || p.kind}{p.capability ? ` · ${p.capability}` : ""}{p.customer_name ? ` · ${p.customer_name}` : ""}</span>
                    <div className="text-[11px] text-muted-foreground">Proposed by {p.proposed_by} ({p.proposed_role})</div>
                  </div>
                  {canEdit ? (
                    <Button size="sm" variant="outline" onClick={() => setResolving(p)} data-testid="tech-proposal-resolve">Review</Button>
                  ) : <Badge className="bg-amber-100 text-amber-800 border-amber-200">Awaiting admin</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="w-56 space-y-1"><Label className="text-xs">Catalog type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="tech-catalog-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((x) => <SelectItem key={x.k} value={x.k}>{x.label}</SelectItem>)}</SelectContent></Select></div>
          {canEdit && <Button className="bg-navy text-white hover:bg-navy-700" onClick={() => setEditing({ kind, value: "", capabilities: [], default_evidence_sources: [], is_microsoft: false, commonly_used: true })} data-testid="tech-catalog-add"><Plus className="h-4 w-4 mr-1.5" /> Add Entry</Button>}
        </div>

        <div className="rounded-md border border-border overflow-hidden">
          <div className="overflow-x-auto max-h-96 overflow-y-auto"><table className="w-full text-sm">
            <thead className="sticky top-0"><tr className="bg-slate-50 border-b border-border text-left">
              {(kind === "product" ? ["Value", "Vendor", "Category", "MS", "Capabilities", ""] : ["Value", ""]).map((h) => <th key={h} className="px-3 py-2.5 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-sm">No entries.</td></tr> :
                items.map((it) => (
                  <tr key={it.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid="tech-catalog-row">
                    <td className="px-3 py-2 font-medium text-navy">{it.value}</td>
                    {kind === "product" && <>
                      <td className="px-3 py-2 text-xs">{it.vendor || "—"}</td>
                      <td className="px-3 py-2 text-xs">{it.system_category || "—"}</td>
                      <td className="px-3 py-2">{it.is_microsoft ? <Cloud className="h-4 w-4 text-blue-600" /> : ""}</td>
                      <td className="px-3 py-2 text-xs">{(it.capabilities || []).join(", ") || "—"}</td>
                    </>}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => setEditing(it)} data-testid="tech-catalog-edit"><Pencil className="h-3.5 w-3.5" /></Button>}
                      {canArchive && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => archive(it)} data-testid="tech-catalog-archive"><Archive className="h-3.5 w-3.5" /></Button>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table></div>
        </div>
      </div>

      <CatalogItemDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} evidenceOptions={items.filter((i) => i.kind === "evidence_source").map((i) => i.value)} />
      <ResolveProposalDialog proposal={resolving} catalogItems={items} onClose={() => setResolving(null)} onResolved={() => { setResolving(null); loadProposals(); load(); }} />
    </section>
  );
}

function CatalogItemDialog({ item, onClose, onSaved, evidenceOptions }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(item || {}); }, [item]);
  if (!item) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isProduct = item.kind === "product";
  const save = async () => {
    if (!f.value?.trim()) { toast.error("Value is required."); return; }
    const body = { kind: item.kind, value: f.value.trim(), vendor: f.vendor || "", system_category: f.system_category || "",
      is_microsoft: !!f.is_microsoft, capabilities: f.capabilities || [], default_evidence_sources: f.default_evidence_sources || [],
      commonly_used: f.commonly_used !== false, deprecated: !!f.deprecated };
    try {
      if (item.id) await api.put(`/blueprints/tech-catalog/${item.id}`, body);
      else await api.post("/blueprints/tech-catalog", body);
      toast.success("Saved."); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="tech-catalog-dialog">
        <DialogHeader><DialogTitle>{item.id ? "Edit" : "Add"} catalog entry</DialogTitle>
          <DialogDescription>{KINDS.find((x) => x.k === item.kind)?.label}</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5"><Label className="text-xs">Value</Label><Input value={f.value || ""} onChange={(e) => set("value", e.target.value)} data-testid="tech-catalog-value" /></div>
          {isProduct && <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Vendor</Label><Input value={f.vendor || ""} onChange={(e) => set("vendor", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs">System category</Label><Input value={f.system_category || ""} onChange={(e) => set("system_category", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={!!f.is_microsoft} onCheckedChange={(v) => set("is_microsoft", !!v)} /> Microsoft-native</label>
            <div className="space-y-1.5"><Label className="text-xs">Capabilities</Label>
              <MultiCombobox value={f.capabilities || []} onChange={(v) => set("capabilities", v)} options={TECH_CAPABILITIES} placeholder="Map to capabilities…" testid="tech-catalog-caps" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Default evidence sources</Label>
              <MultiCombobox value={f.default_evidence_sources || []} onChange={(v) => set("default_evidence_sources", v)} options={evidenceOptions} allowCustom customLabel="Add" placeholder="Suggested evidence…" testid="tech-catalog-evsrc" /></div>
          </>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={save} data-testid="tech-catalog-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResolveProposalDialog({ proposal, catalogItems, onClose, onResolved }) {
  const [action, setAction] = useState("approve");
  const [mergeInto, setMergeInto] = useState("");
  const [caps, setCaps] = useState([]);
  const [vendor, setVendor] = useState("");
  const [category, setCategory] = useState("");
  const [isMs, setIsMs] = useState(false);
  useEffect(() => { if (proposal) { setAction("approve"); setMergeInto(""); setCaps(proposal.capability ? [proposal.capability] : []); setVendor(""); setCategory(""); setIsMs(false); } }, [proposal]);
  if (!proposal) return null;
  const resolve = async () => {
    try {
      await api.post(`/blueprints/tech-catalog/proposals/${proposal.id}/resolve`, {
        action, merge_into: mergeInto, capabilities: caps, vendor, system_category: category, is_microsoft: isMs });
      toast.success("Proposal resolved."); onResolved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  return (
    <Dialog open={!!proposal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="tech-resolve-dialog">
        <DialogHeader><DialogTitle>Review proposed value</DialogTitle>
          <DialogDescription>"{proposal.value}" — proposed for {proposal.customer_name || "a customer"} by {proposal.proposed_by}.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5"><Label className="text-xs">Decision</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="tech-resolve-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approve">Approve into shared catalog</SelectItem>
                <SelectItem value="merge">Merge with existing entry</SelectItem>
                <SelectItem value="keep_customer">Keep customer-specific (no catalog change)</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
              </SelectContent></Select></div>
          {action === "approve" && proposal.kind === "product" && <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Vendor</Label><Input value={vendor} onChange={(e) => setVendor(e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={isMs} onCheckedChange={(v) => setIsMs(!!v)} /> Microsoft-native</label>
            <div className="space-y-1.5"><Label className="text-xs">Capabilities</Label>
              <MultiCombobox value={caps} onChange={setCaps} options={TECH_CAPABILITIES} placeholder="Map to capabilities…" /></div>
          </>}
          {action === "merge" && (
            <div className="space-y-1.5"><Label className="text-xs">Merge into existing entry</Label>
              <Combobox value={mergeInto} onChange={setMergeInto} options={catalogItems.filter((i) => i.kind === proposal.kind).map((i) => i.value)} placeholder="Select existing entry…" testid="tech-resolve-merge" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={resolve} data-testid="tech-resolve-confirm"><Check className="h-4 w-4 mr-1.5" /> Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
